import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import { numeroMonetarioParaExtenso } from "../../../../utils/numeroPorExtenso";

/** ---------------------- Tipos ---------------------- */

type EstoqueDepartamentoKey =
  | "suprimentos"
  | "recursos-humanos"
  | "suprimentos-campos"
  | "tecnicos"
  | "generico";

type EstoqueConfig = {
  key: EstoqueDepartamentoKey;
  slug: string;
  departamento: string;
  title: string;
  reportTitle: string;
  fileNamePrefix: string;
  movimentacaoPath: string;
  detalheProdutoPath: (codigo: string) => string;
};

export type EstoqueProduto = {
  posicao: string | null;
  codigo: string;
  ncm: string | null;
  nomeProduto: string;
  custoUnitario: string | null;
  quantidade: number | null;
  classificacao: string | null;
  moeda: string | null;
};

export type EstoqueRowAPI = {
  codigo: string;
  nome: string;
  classificacao: string | null;
  posicao: string | null;
  quantidade_atual: number | null;
  custo_unitario_atual: string | null;
  moeda?: string | null;
  ncm?: string | null;
};

type ListaResp = {
  ok?: boolean;
  fonte?: "cache" | "banco";
  versao?: number | null;
  departamento?: string;
  estoque_slug?: string | null;
  data: EstoqueRowAPI[];
};

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

/** ---------------------- Config por slug ---------------------- */

const ESTOQUE_CONFIGS: EstoqueConfig[] = [
  {
    key: "suprimentos",
    slug: "estoque-p-servico-e-comercial",
    departamento: "suprimentos",
    title: "Suprimentos - Estoque p/ Serviço e Comercial",
    reportTitle: "Relatório de Estoque - Suprimentos",
    fileNamePrefix: "Suprimentos-Estoque",
    movimentacaoPath:
      "/suprimentos/estoque-p-servico-e-comercial/estoque/realizar-movimentacao",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-servico-e-comercial/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    key: "suprimentos-campos",
    slug: "estoque-p-servico-campos",
    departamento: "suprimentos-campos",
    title: "Suprimentos - Materiais do Estoque p/ Serviço Campos",
    reportTitle: "Relatório de Estoque - Suprimentos Campos",
    fileNamePrefix: "Suprimentos-Campos-Estoque",
    movimentacaoPath:
      "/suprimentos/estoque-p-servico-campos/estoque/realizar-movimentacao",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-servico-campos/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    key: "recursos-humanos",
    slug: "estoque-p-uso-coorporativo",
    departamento: "recursos-humanos",
    title: "Suprimentos - Estoque p/ Uso Coorporativo",
    reportTitle: "Relatório de Estoque - Estoque p/ Uso Coorporativo",
    fileNamePrefix: "Suprimentos-Estoque-Uso-Coorporativo",
    movimentacaoPath:
      "/suprimentos/estoque-p-uso-coorporativo/estoque/realizar-movimentacao",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-uso-coorporativo/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    key: "tecnicos",
    slug: "estoque-p-tecnicos-e-laboratorio",
    departamento: "tecnicos",
    title: "Laboratório - Estoque",
    reportTitle: "Relatório de Estoque - Laboratório",
    fileNamePrefix: "Laboratorio-Estoque",
    movimentacaoPath:
      "/suprimentos/estoque-p-tecnicos-e-laboratorio/estoque/realizar-movimentacao",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-tecnicos-e-laboratorio/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
];

function slugToTitle(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part.toLowerCase() === "p") return "p/";
      if (part.toLowerCase() === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function getConfigBySlug(estoqueSlug?: string): EstoqueConfig {
  const slug = normalizeSlug(estoqueSlug);

  const found = ESTOQUE_CONFIGS.find((config) => config.slug === slug);

  if (found) {
    return found;
  }

  const safeSlug = slug || "estoque";

  const titleFromSlug = slugToTitle(safeSlug);

  return {
    key: "generico",
    slug: safeSlug,

    /**
     * IMPORTANTE:
     * Para estoque totalmente dinâmico, o ideal é o backend também receber
     * estoque_slug e usar isso para filtrar o estoque correto.
     *
     * Enquanto o backend ainda depender apenas de "departamento",
     * este campo vai como o próprio slug.
     */
    departamento: safeSlug,

    title: `Suprimentos - ${titleFromSlug}`,
    reportTitle: `Relatório de Estoque - ${titleFromSlug}`,
    fileNamePrefix: `Estoque-${safeSlug}`,
    movimentacaoPath: `/suprimentos/${safeSlug}/estoque/realizar-movimentacao`,
    detalheProdutoPath: (codigo) =>
      `/suprimentos/${safeSlug}/produtos/${encodeURIComponent(codigo)}`,
  };
}

/** ---------------------- Constantes API ---------------------- */

const URL_ESTOQUE =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-estoque";

const URL_AWAIT_BASE = "https://api.eva-googlemarine.com/departamentos";

const ACCESS_TOKEN_KEY = "access_token";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function sameArray(a: EstoqueProduto[], b: EstoqueProduto[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function normalizeRow(r: EstoqueRowAPI): EstoqueProduto {
  return {
    posicao: r.posicao ?? null,
    codigo: r.codigo ?? "",
    ncm: r.ncm ?? null,
    nomeProduto: r.nome ?? "",
    custoUnitario: r.custo_unitario_atual ?? null,
    quantidade: r.quantidade_atual ?? null,
    classificacao: r.classificacao ?? null,
    moeda:
      typeof r.moeda === "string" && r.moeda.trim() !== "" ? r.moeda : null,
  };
}

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function moedaFromItem(p: EstoqueProduto): string | null {
  return p?.moeda && String(p.moeda).trim() !== "" ? String(p.moeda) : null;
}

function moedaSymbol(moeda?: string | null): string {
  if (moeda === "USD") return "$";
  if (moeda === "EUR") return "€";
  if (moeda === "BRL") return "R$";
  return "";
}

function formatCurrency(
  valor: string | number | null | undefined,
  moeda?: string | null
): string {
  if (!moeda) return "N/A";

  const n = typeof valor === "string" ? Number(valor) : valor ?? 0;
  if (!isFinite(n)) return "N/A";

  const symbol = moedaSymbol(moeda);
  if (!symbol) return "N/A";

  return `${symbol} ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function buildRow(p: EstoqueProduto): string[] {
  const moeda = moedaFromItem(p);

  return [
    p.posicao ?? "",
    p.codigo ?? "",
    p.ncm ?? "",
    p.nomeProduto ?? "",
    formatCurrency(p.custoUnitario, moeda),
    moeda ?? "N/A",
    String(p.quantidade ?? 0),
    p.classificacao ?? "",
  ];
}

function rowMatchesFilters(
  row: (string | number | React.ReactNode)[],
  filters: string[]
) {
  return row.every((cell, i) => {
    const filtro = normalizeText(filters[i] ?? "");
    if (!filtro) return true;

    const valor =
      typeof cell === "string" || typeof cell === "number"
        ? normalizeText(cell)
        : "";

    return valor.includes(filtro);
  });
}

/** ---------------------- HTTP helpers ---------------------- */

async function headVersao(
  departamento: string,
  estoqueSlug: string,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    const params = new URLSearchParams();

    params.set("departamento", departamento);
    params.set("estoque_slug", estoqueSlug);

    const url = `${URL_ESTOQUE}?${params.toString()}`;

    const res = await fetch(url, {
      method: "HEAD",
      headers: buildAuthHeaders(false),
      signal,
    });

    if (!res.ok) return null;

    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;

    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  departamento: string,
  estoqueSlug: string,
  _versaoAtual: number | null,
  signal?: AbortSignal
): Promise<{
  fonte: "cache" | "banco" | undefined;
  versao: number | null;
  data: EstoqueProduto[];
}> {
  const res = await fetch(URL_ESTOQUE, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    signal,
    body: JSON.stringify({
      departamento,
      estoque_slug: estoqueSlug,
      estoqueSlug,
    }),
  });

  if (!res.ok) {
    throw new Error(`Erro HTTP ${res.status}`);
  }

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;

  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = (await res.json()) as ListaResp;

  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr as number) ? verHdr : verBody;

  const data: EstoqueProduto[] = Array.isArray(json?.data)
    ? json.data.map(normalizeRow)
    : [];

  return {
    fonte,
    versao: versao ?? null,
    data,
  };
}

async function awaitChange(
  departamento: string,
  estoqueSlug: string,
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const params = new URLSearchParams();

    params.set("since", String(since));
    params.set("timeout_ms", String(AWAIT_TIMEOUT_MS));
    params.set("estoque_slug", estoqueSlug);

    const url = `${URL_AWAIT_BASE}/${encodeURIComponent(
      departamento
    )}/estoque/await?${params.toString()}`;

    const res = await fetch(url, {
      method: "GET",
      headers: buildAuthHeaders(false),
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();

    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;

    const changed = Boolean(json?.changed);

    return {
      changed,
      versao,
    };
  } catch {
    return null;
  }
}

/** ---------------------- Hook genérico ---------------------- */

function useEstoqueDisponivel(departamento: string, estoqueSlug: string) {
  const [itens, setItens] = useState<EstoqueProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, setVersao] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);
  const fetching = useRef(false);

  const awaitSupported = useRef<boolean | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitInFlightRef = useRef(false);

  const setVersaoBoth = (v: number | null) => {
    versaoRef.current = v;
    setVersao(v);
  };

  async function carregar(signal?: AbortSignal) {
    fetching.current = true;

    try {
      setErro(null);

      const result = await getLista(
        departamento,
        estoqueSlug,
        versaoRef.current,
        signal
      );

      setFonte(result.fonte);
      setVersaoBoth(result.versao);

      setItens((prev) => {
        if (sameArray(prev, result.data)) return prev;
        return result.data;
      });
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar estoque.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    setLoading(true);
    setErro(null);
    setItens([]);

    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();
    setVersaoBoth(null);

    const bootstrapController = new AbortController();

    (async () => {
      try {
        const head = await headVersao(
          departamento,
          estoqueSlug,
          bootstrapController.signal
        );

        if (head !== null) {
          setVersaoBoth(head);
        }

        await carregar(bootstrapController.signal);
        setLoading(false);

        const init = await awaitChange(departamento, estoqueSlug, -1);

        if (init && typeof init.versao === "number") {
          setVersaoBoth(init.versao);
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur !== null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();

            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;

            try {
              resp = await awaitChange(
                departamento,
                estoqueSlug,
                cur,
                ac.signal
              );
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(ERROR_BACKOFF_MS);
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }

              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              setVersaoBoth(resp.versao ?? cur);
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useEstoqueDisponivel loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      bootstrapController.abort();
      awaitCtrlRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departamento, estoqueSlug]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    itens,
    loading,
    fonte,
    versao,
    erro,
    refresh,
  };
}

/** ---------------------- Página genérica ---------------------- */

const EstoqueGenerico: React.FC = () => {
  const { estoqueSlug } = useParams<{ estoqueSlug: string }>();
  const navigate = useNavigate();

  const config = useMemo(() => getConfigBySlug(estoqueSlug), [estoqueSlug]);

  const { itens, loading, erro } = useEstoqueDisponivel(
    config.departamento,
    config.slug
  );

  const headers = useMemo(
    () => [
      "Posição",
      "Código",
      "NCM",
      "Nome do Produto",
      "Custo Unitário",
      "Moeda",
      "Quantidade",
      "Classificação",
    ],
    []
  );

  const columnWidths = useMemo(
    () => ["10px", "70px", "70px", "350px", "100px", "60px", "10px", "50px"],
    []
  );

  const [mostrarZerados, setMostrarZerados] = useState(false);
  const [revelarZeradosNosFiltros, setRevelarZeradosNosFiltros] =
    useState(false);

  const [tableFilters, setTableFilters] = useState<string[]>(
    headers.map(() => "")
  );

  const [selectedColumns] = useState<boolean[]>(headers.map(() => true));

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const filtrosAtivos = useMemo(
    () => tableFilters.some((f) => normalizeText(f) !== ""),
    [tableFilters]
  );

  useEffect(() => {
    if (!filtrosAtivos) {
      setRevelarZeradosNosFiltros(false);
    }
  }, [filtrosAtivos]);

  const itensComRows = useMemo(
    () =>
      itens.map((item) => ({
        item,
        row: buildRow(item),
      })),
    [itens]
  );

  const itensFiltradosPelosCamposDaTabela = useMemo(() => {
    return itensComRows.filter(({ row }) =>
      rowMatchesFilters(row, tableFilters)
    );
  }, [itensComRows, tableFilters]);

  const deveMostrarZerados =
    mostrarZerados || (filtrosAtivos && revelarZeradosNosFiltros);

  const itensVisiveis = useMemo(() => {
    if (deveMostrarZerados) {
      return itensFiltradosPelosCamposDaTabela.map(({ item }) => item);
    }

    return itensFiltradosPelosCamposDaTabela
      .map(({ item }) => item)
      .filter((p) => (p.quantidade ?? 0) > 0);
  }, [itensFiltradosPelosCamposDaTabela, deveMostrarZerados]);

  const rows = useMemo(() => itensVisiveis.map(buildRow), [itensVisiveis]);

  const existeSomenteZeradoNosFiltros = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return false;

    const encontrados = itensFiltradosPelosCamposDaTabela.map(
      ({ item }) => item
    );

    if (!encontrados.length) return false;

    const comSaldo = encontrados.some((p) => (p.quantidade ?? 0) > 0);
    const zerados = encontrados.some((p) => (p.quantidade ?? 0) <= 0);

    return !comSaldo && zerados;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const quantidadeItensZeradosEncontrados = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return 0;

    return itensFiltradosPelosCamposDaTabela.filter(
      ({ item }) => (item.quantidade ?? 0) <= 0
    ).length;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const itensRelatorio = useMemo(
    () => itens.filter((p) => (p.quantidade ?? 0) > 0),
    [itens]
  );

  const rowsReport = useMemo(
    () => itensRelatorio.map(buildRow),
    [itensRelatorio]
  );

  const totalProdutosRel = useMemo(
    () => itensRelatorio.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itensRelatorio]
  );

  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    itensRelatorio.forEach((p) => {
      const moeda = moedaFromItem(p);
      if (!moeda) return;

      const custo = Number(p.custoUnitario ?? 0);
      const qtd = p.quantidade ?? 0;
      const subtotal = isFinite(custo * qtd) ? custo * qtd : 0;

      if (!acc[moeda]) acc[moeda] = 0;
      acc[moeda] += subtotal;
    });

    return acc;
  }, [itensRelatorio]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Produtos no Estoque: ${totalProdutosRel}`);

    const entries = Object.entries(totaisPorMoeda);
    if (!entries.length) return lines;

    entries.sort(([a], [b]) => a.localeCompare(b));

    entries.forEach(([moeda, total]) => {
      const symbol = moedaSymbol(moeda);

      const formatted = `${symbol ? symbol + " " : ""}${total.toLocaleString(
        "pt-BR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;

      const extensoMoeda = numeroMonetarioParaExtenso(total, moeda);

      lines.push(
        `Valor Total em Estoque (${moeda}): ${formatted} (${extensoMoeda})`
      );
    });

    return lines;
  }, [totalProdutosRel, totaisPorMoeda]);

  const codigoIdx = headers.indexOf("Código");

  const gerarRelatorioEstoque = () => {
    generatePdfReport({
      reportTitle: config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações dos Produtos no Estoque",
      headers,
      rows: rowsReport,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines,
    });
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        departamento?: {
          id?: number | string | null;
          name?: string;
          slug?: string | null;
          routeKey?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = window.location.pathname.replace(/\/+$/, "");
      const basePath = `/suprimentos/${config.slug}`;

      const pertenceAoEstoqueAtual =
        currentPath === basePath ||
        currentPath === `${basePath}/estoque` ||
        currentPath.startsWith(`${basePath}/`);

      if (!pertenceAoEstoqueAtual) return;

      gerarRelatorioEstoque();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config, headers, rowsReport, enabledColumnIndexes, footerLines]);

  const handleToggleMostrarZerados = () => {
    setMostrarZerados((prev) => !prev);
  };

  const handleMostrarZeradosSomenteNosFiltros = () => {
    setRevelarZeradosNosFiltros(true);
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={config.title} />

      {existeSomenteZeradoNosFiltros && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #e7cf9a",
            background: "#fff8e8",
            color: "#6b4f1f",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            Você possui este item ou similar com quantidade 0.
            {quantidadeItensZeradosEncontrados > 1
              ? ` Foram encontrados ${quantidadeItensZeradosEncontrados} itens zerados.`
              : ""}{" "}
            Clique em "Mostrar Itens Zerados" para exibir.
          </div>

          <button
            type="button"
            onClick={handleMostrarZeradosSomenteNosFiltros}
            style={{
              height: 34,
              padding: "0 14px",
              border: "1px solid #c9ad66",
              background: "#ffffff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#6b4f1f",
              whiteSpace: "nowrap",
            }}
          >
            Mostrar Itens Zerados
          </button>
        </div>
      )}

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando estoque…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          filters={tableFilters}
          onFiltersChange={setTableFilters}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "");

            if (codigo) {
              navigate(config.detalheProdutoPath(codigo));
            }
          }}
          responsiveWidths={{
            1300: {
              1: "10px",
              2: "70px",
              3: "0px",
              4: "350px",
              5: "100px",
              6: "60px",
              7: "10px",
              8: "50px",
            },
          }}
        />
      )}
    </div>
  );
};

export default EstoqueGenerico;
